import { computed, effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { Correspondence, CorrespondenceApi } from './correspondence.api';
import { WorkflowIntegrationResource } from '../workflow';
import { MemberStore } from '../member/member-store';
import { fromISO } from '@nestech/utils';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class CorrespondenceStore {
  readonly #api = inject(CorrespondenceApi);

  integration: WorkflowIntegrationResource<Correspondence>;
  start = computed(() => this.integration.resourceValue()?.start);
  sign = computed(() => this.integration.resourceValue()?.sign);
  isLoading: Signal<boolean>;

  markingNewMessages = signal<boolean>(true);
  messageRead: () => void;
  createIfNeeded: () => Promise<'existing' | 'created'>;

  constructor() {
    const memberStore = inject(MemberStore);
    this.integration = new WorkflowIntegrationResource(this.#api, () => this.#api.get());
    this.isLoading = this.integration.isLoading;
    this.createIfNeeded = () =>
      this.#api
        .get()
        .then(() => 'existing' as const)
        .catch(() => this.#api.create().then(() => 'created' as const));
    effect(() => {
      const wf = untracked(this.integration.resourceValue);
      const event = this.#api.workflowChanged();
      (event?.operation === 'Create' || event?.operation === 'Delete' || (wf && event && wf.id === event.id)) &&
        this.integration.reload();
    });
    effect(() => {
      const latestMessage = this.integration.resourceValue()?.start?.formTables.chat.tableData.at(-1);
      const dismissedDate = fromISO(
        untracked(memberStore.preferences.value)?.notifications?.['NEW_CHAT_MESSAGE']?.dismissedDate,
      );
      if (
        dismissedDate?.isValid &&
        latestMessage?.author &&
        DateTime.fromJSDate(latestMessage.timestamp) > dismissedDate
      ) {
        untracked(this.markingNewMessages) &&
          memberStore.patchPreferences({
            notifications: {
              NEW_CHAT_MESSAGE: { dismissedDate: '' },
            },
          });
      }
    });
    this.messageRead = () => {
      memberStore.patchPreferences({
        notifications: {
          NEW_CHAT_MESSAGE: { dismissedDate: new Date().toISOString() },
        },
      });
    };
  }
}
